/**
 * useFacilitiesCalc — wire facility SoT data + the Schedule form's
 * relativity factors to the pure pricing functions in `domain/pricing`
 * and produce one `FacilityCalcResult` per facility row.
 *
 * Inputs:
 *   - `facilities`: the canonical Facility[] from `useFacilities`. The
 *     hook is intentionally agnostic to where they come from (form
 *     state, server, mock) — it just transforms.
 *   - Relativity factors: read from the surrounding Schedule
 *     RHF context (the only place they live).
 *   - Rates table: from `useRates()` (cached).
 *
 * Adapter responsibilities (kept here, OUT of the domain layer):
 *   - Facility stores numbers as strings — parse them.
 *   - Facility `geo` is "Rural" | "Urban" | "" — map to the rates-
 *     lookup convention ("ros" | "urban") via `composeRateCode`.
 *   - `actuarialRate` and `sirCredit` (both stored on the location by the
 *     pricing-model import) are passed through as `rateOverride` and
 *     `sirCreditOverride`, so each lookup is the fallback rather than the
 *     first answer.
 *   - Maturity Credit is entered as a percent integer (e.g. "80");
 *     convert to a 0–1 fraction. Empty defaults to 1.0 so an
 *     unfilled row doesn't accidentally zero out the premium.
 *   - Memory Care fields on the SoT are intentionally ignored — the
 *     UW pricing directive defines Equivalency / Total Lic over the
 *     4-census / 3-licensed buckets only.
 */
import { useDeferredValue, useMemo } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
    RELATIVITY,
    composeRateCode,
    computeFacilityRow,
    type FacilityCalcResult,
    type RelativityFactors,
} from "../../../domain/pricing";
import { useRates } from "../../../hooks/use-rates";
import { useSirCredits } from "../../../hooks/use-sir-credits";
import type { Facility } from "../../../types/facility";
import type { ScheduleFormData } from "./schema";

const EMPTY_RESULT: FacilityCalcResult = {
    occupancy: null,
    equivalency: 0,
    rate: null,
    unadjPremium: null,
    matAdjPremium: null,
    sirCredit: null,
    sirAdjPremium: null,
    totalLicensed: 0,
};

function num(v: string | undefined | null): number {
    if (!v) return 0;
    const cleaned = String(v).replace(/[^\d.-]/g, "");
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : 0;
}

function geoToRos(geo: string | undefined): "ros" | "urban" {
    return geo === "Rural" ? "ros" : "urban";
}

/** A stored numeric cell, or null when it is blank or unparseable.
 *  Distinct from `num()`, which collapses a blank to 0 — a blank has to
 *  stay null so the caller can tell "nothing stored" (fall back) from a
 *  real zero (use it). */
export function parseStoredNumber(raw: string | undefined): number | null {
    // Strip only the formatting a money cell carries and let `Number` judge
    // the rest. Stripping every non-digit instead would leave "12x" as 12 and
    // "N/A" as `Number("")`, which is 0 — both then win over the fallback as
    // if the row had stored a real value.
    const cleaned = (raw ?? "").replace(/[$,\s]/g, "");
    if (cleaned === "") return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
}

function parseMaturityCredit(raw: string | undefined): number {
    const trimmed = (raw ?? "").trim();
    if (trimmed === "") return 1.0;
    return num(trimmed) / 100;
}

export function useFacilitiesCalc(facilities: Facility[]): FacilityCalcResult[] {
    const { control } = useFormContext<ScheduleFormData>();
    const relativityForm = useWatch({ control, name: "relativity" });
    const ratesQuery = useRates();
    const sirCreditsQuery = useSirCredits();

    // Pricing recompute (computeFacilityRow over every row, with rate +
    // SIR-table lookups) is the heavy part. Defer it so a keystroke paints
    // immediately and the calc settles in a low-priority pass — typing stays
    // native, derived columns catch up a beat later. Index alignment holds
    // while typing (row count is stable); the deferred value catches up at
    // once on add/remove since those aren't rapid.
    const deferredFacilities = useDeferredValue(facilities);

    const relativity = useMemo<RelativityFactors>(
        () => ({
            // Form overrides take priority; fall back to the canonical
            // RELATIVITY constants whenever the form leaves a slot blank.
            // `??` on a null-returning parse, not `||` on `num()`: an
            // imported factor of exactly 0 is a real weighting and must not
            // fall through to the constant.
            skilled: parseStoredNumber(relativityForm?.skilled) ?? RELATIVITY.skilled,
            assisted: parseStoredNumber(relativityForm?.assisted) ?? RELATIVITY.assisted,
            independent: parseStoredNumber(relativityForm?.independent) ?? RELATIVITY.independent,
            addl: parseStoredNumber(relativityForm?.additionalExposure) ?? RELATIVITY.addl,
        }),
        [relativityForm],
    );

    return useMemo(() => {
        if (ratesQuery.status !== "ready" || sirCreditsQuery.status !== "ready") {
            return deferredFacilities.map(() => EMPTY_RESULT);
        }
        const refs = {
            rates: ratesQuery.data,
            relativity,
            sirTable: sirCreditsQuery.data,
        };
        return deferredFacilities.map((f) =>
            computeFacilityRow(
                {
                    census: {
                        skilled: num(f.censusSkilled),
                        assisted: num(f.censusAssisted),
                        independent: num(f.censusIndependent),
                        addl: num(f.censusAddlExposure),
                    },
                    licensed: {
                        snf: num(f.licensedSnf),
                        alf: num(f.licensedAlf),
                        ilf: num(f.licensedIlf),
                    },
                    rateCode: composeRateCode(f.state, geoToRos(f.geo)),
                    // An imported pricing model already carries its own
                    // rate; it wins until the row's state or geo changes,
                    // at which point the Schedule clears it (see
                    // `facility-schedule.tsx`) and the lookup takes over.
                    rateOverride: parseStoredNumber(f.actuarialRate),
                    // Blank retention → NaN so the SIR credit stays a dash;
                    // an explicit $0 selection → 0 → real 0% tier (see lookupSirCredit).
                    retention: f.retention?.trim() ? num(f.retention) : NaN,
                    // Like the rate: the sheet's own credit wins until the
                    // user edits the retention it was priced against, which
                    // is when the Schedule clears it.
                    sirCreditOverride: parseStoredNumber(f.sirCredit),
                    maturityCredit: parseMaturityCredit(f.maturityCredit),
                },
                refs,
            ),
        );
    }, [deferredFacilities, ratesQuery, sirCreditsQuery, relativity]);
}
